import { Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { MonthlyData } from "../types";
import { TOYOTA_RED, INFO_BLUE, SUCCESS_GREEN } from "../theme";
import KpiCard from "../components/KpiCard";
import PlotlyChart from "../components/PlotlyChart";
import ChartCard from "../components/ChartCard";
import TableCard from "../components/TableCard";

interface Props { data: MonthlyData; compact?: boolean; }

const ProdWorkloads = ({ data, compact }: Props) => {
  const totalWorkloads = data.prodWorkloads.reduce((s, w) => s + w.count, 0);
  const netChange = data.prodWorkloads.reduce((s, w) => s + w.diff, 0);
  const added = data.prodWorkloads.filter((w) => w.diff > 0).reduce((s, w) => s + w.diff, 0);
  const removed = Math.abs(data.prodWorkloads.filter((w) => w.diff < 0).reduce((s, w) => s + w.diff, 0));

  const sp = compact ? 1 : 2;
  const chartH = compact ? 280 : 350;

  return (
    <Box>
      {!compact && (
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#1B2559" }}>
          🚀 Production Workloads
        </Typography>
      )}

      {/* KPI Cards */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Total Workloads" value={totalWorkloads} color={TOYOTA_RED} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Net Change" value={netChange} delta={`↑ ${netChange}`} deltaType={netChange >= 0 ? "positive" : "negative"} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Added" value={added} color={SUCCESS_GREEN} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Removed" value={removed} color={TOYOTA_RED} />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Workload Count by Type">
            <PlotlyChart
              data={[{
                x: data.prodWorkloads.map((w) => w.type),
                y: data.prodWorkloads.map((w) => w.count),
                type: "bar",
                name: "PROD",
                marker: { color: TOYOTA_RED },
              }]}
              layout={{ xaxis: { title: "PROD", tickangle: -30 }, yaxis: { title: "Count" } }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Month-over-Month Change">
            <PlotlyChart
              data={[{
                x: data.prodWorkloads.map((w) => w.type),
                y: data.prodWorkloads.map((w) => w.diff),
                type: "bar",
                name: "PROD",
                marker: { color: INFO_BLUE },
              }]}
              layout={{ xaxis: { title: "PROD", tickangle: -30 }, yaxis: { title: "Diff" } }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Workload Details Table */}
      <TableCard title="Workload Details" titleColor={TOYOTA_RED}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Workload Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Count</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.prodWorkloads
              .slice()
              .sort((a, b) => b.count - a.count)
              .map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                  <TableCell align="right" sx={{ color: row.diff >= 0 ? SUCCESS_GREEN : TOYOTA_RED }}>
                    {row.diff}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableCard>
    </Box>
  );
};

export default ProdWorkloads;
